import { useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";

import heroBgImg from "../assets/hero.webp";
import logoImg from "../assets/marca/logo-beige.svg";

function ArrowIcon() {
  return (
    <svg
      className="shrink-0"
      width="20"
      height="20"
      viewBox="0 0 14 10"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 5H13M13 5L9 1M13 5L9 9"
        stroke="#F2F1DE"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

interface HeroProps {
  className?: string;
}

export default function Hero({ className = "" }: HeroProps) {
  const heroRef = useRef<HTMLElement>(null);

  const { scrollY } = useScroll({
    layoutEffect: false,
  });

  const imageY = useTransform(scrollY, [0, 1500], [0, 260]);
  const contentY = useTransform(scrollY, [0, 1500], [0, -90]);
  const imageScale = useTransform(scrollY, [0, 1500], [1.08, 1.18]);
  const overlayOpacity = useTransform(scrollY, [0, 1200], [0.28, 0.42]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);

    if (element) {
      const navbarOffset = 96;
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementTop - navbarOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <section
      id="inicio"
      ref={heroRef}
      className={`relative min-h-[100svh] w-full overflow-hidden ${className}`}
      data-name="Hero Section"
    >
      <motion.div
        className="absolute inset-0 h-full w-full"
        style={{ y: imageY, scale: imageScale }}
        aria-hidden="true"
        initial={{ scale: 1.14, opacity: 0.85 }}
        animate={{ scale: 1.08, opacity: 1 }}
        transition={{
          duration: 1.4,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        <img src={heroBgImg} alt="" className="h-full w-full object-cover" />

        <motion.div
          className="absolute inset-0 bg-black"
          style={{ opacity: overlayOpacity }}
        />
      </motion.div>

      <motion.div
        className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-[1920px] px-4 sm:px-6 md:px-8 lg:px-10 xl:px-16"
        style={{ y: contentY }}
      >
        <div className="flex w-full items-center py-[max(88px,8svh)] sm:py-[max(104px,9svh)] lg:py-[max(112px,9svh)]">
          <div className="flex w-full max-w-[1120px] flex-col items-start justify-center gap-6 sm:gap-8 lg:gap-6">
            <motion.div
              initial={{ opacity: 0, y: -28, rotate: -2 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.15,
              }}
              className="shrink-0"
            >
              <motion.img
                src={logoImg}
                alt="Stacy Logo"
                className="hero-logo h-auto w-[clamp(88px,12vw,180px)]"
                initial={{ scale: 0.92 }}
                animate={{ scale: 1 }}
                transition={{
                  duration: 0.8,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.2,
                }}
              />
            </motion.div>

            <div className="flex w-full flex-col gap-6 sm:gap-8 lg:gap-6">
              <motion.div
                initial={{ opacity: 0, y: 42 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.95,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.3,
                }}
                className="flex flex-col gap-4 text-[#F2F1DE] lg:gap-3"
              >
                <motion.div
                  className="font-['Excessive_Extra_Condensed'] uppercase leading-[0.9] not-italic text-[#F2F1DE]"
                  initial={{ opacity: 0, y: 36 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.9,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.35,
                  }}
                >
                  {/* Mobile */}
                  <div className="block md:hidden text-[clamp(4.5rem,min(37vw,17vh),9.125rem)] tracking-[0.02em]">
                    <p className="m-0">SOMOS LA EXCUSA</p>
                    <p className="m-0">PARA VOLVER</p>
                    <p className="m-0">A JUGAR</p>
                  </div>

                  {/* Tablet */}
                  <div className="hidden md:block lg:hidden text-[clamp(7rem,min(26vw,20vh),12.5rem)] tracking-[0.02em]">
                    <p className="m-0 whitespace-nowrap">SOMOS LA EXCUSA</p>
                    <p className="m-0 whitespace-nowrap">PARA VOLVER A JUGAR</p>
                  </div>

                  {/* Desktop */}
                  <div className="hidden lg:block text-[clamp(8rem,min(15.5vw,23vh),14rem)] tracking-[0.02em]">
                    <p className="m-0 whitespace-nowrap">SOMOS LA EXCUSA</p>
                    <p className="m-0 whitespace-nowrap">PARA VOLVER A JUGAR</p>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.65,
                }}
                className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4 md:gap-6"
              >
                <motion.a
                  href="https://wa.me/5492613866837?text=%C2%A1Hola%20Stacy!%20Quiero%20hacer%20un%20pedido.%20%C2%BFMe%20comparten%20el%20men%C3%BA%3F"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(186, 70, 67, 0.5)" }}
                  whileTap={{ scale: 0.95 }}
                  className="flex min-h-[64px] w-full items-center justify-center gap-2.5 rounded-lg bg-[#BA4643] px-6 py-4 sm:w-auto sm:min-w-[240px]"
                >
                  <p className="font-['Roboto_Mono',monospace] text-lg font-medium tracking-[0.02em] text-[#F2F1DE] sm:text-xl">
                    PEDÍ AHORA
                  </p>
                  <ArrowIcon />
                </motion.a>

                <motion.button
                  onClick={() => scrollToSection("burgers")}
                  whileHover={{
                    scale: 1.03,
                    backgroundColor: "rgba(242, 241, 222, 0.1)",
                    y: -2,
                  }}
                  whileTap={{ scale: 0.97 }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 20,
                  }}
                  className="flex min-h-[64px] w-full items-center justify-center rounded-lg border-[3px] border-solid border-[#F2F1DE] px-6 py-4 sm:w-auto sm:min-w-[240px]"
                >
                  <span className="font-['Roboto_Mono',monospace] text-lg font-medium tracking-[0.02em] text-[#F2F1DE] sm:text-xl">
                    VER BURGERS
                  </span>
                </motion.button>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}